import React, { useState } from 'react';
import { Modal, Form, Input, message, Select, Row, Col, Typography } from 'antd';
import { api } from '@common/api';
import { InputMask } from '@components/atoms';

const { Title } = Typography;
const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  serviceType?: string;
  projectType?: string;
  message: string;
}

interface ContactExpertsModalProps {
  visible: boolean;
  onClose: () => void;
  productName?: string;
  source?: 'merch' | 'product-detail' | 'landing';
}

export const ContactExpertsModal: React.FC<ContactExpertsModalProps> = ({
  visible,
  onClose,
  productName,
  source = 'merch',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      // Format the message to include all product interests
      let additionalInfo = '';
      
      if (values.serviceType) {
        additionalInfo += `\n\nService Type: ${values.serviceType}`;
      }
      
      if (values.projectType) {
        additionalInfo += `\n\nProject Type: ${values.projectType}`;
      }
      
      // Build subject based on source
      let subject = 'Product Inquiry';
      if (source === 'product-detail' && productName) {
        subject = `Product Inquiry - ${productName}`;
      } else if (source === 'merch') {
        subject = 'Product Inquiry from Shop';
      } else if (source === 'landing') {
        subject = 'Product Inquiry from Landing Page';
      }
      
      const dataToSend = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        subject,
        message: `${values.message}${additionalInfo}`,
      };
      
      await api.post('/contact', dataToSend);
      form.resetFields();
      onClose();
      message.success('Your request has been sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <Title level={3} className="font-azonix !font-normal text-primary uppercase">
          request information
        </Title>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<span className="text-primary text-xl">X</span>}
      centered
      width={600}
      styles={{
        mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)' },
        content: { backgroundColor: '#303030', border: '1px solid #464647', borderRadius: '0px' },
        header: { backgroundColor: '#303030', borderBottom: '1px solid #464647' },
        body: { padding: '12px 24px' },
      }}
    >
      <Form
        form={form}
        name="contact-experts-form"
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        disabled={isSubmitting}
        className="max-h-[85vh] overflow-y-auto overflow-x-hidden"
        style={{
          margin: 0,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Basic Contact Information */}
        <Form.Item
          label={<span className="font-azonix !font-normal text-white uppercase tracking-widest">Full Name</span>}
          name="name"
          rules={[{ required: true, message: 'Please enter your name' }]}
        >
          <Input
            className="bg-zinc-800 font-coda border-zinc-700 text-white"
            placeholder="Your full name"
          />
        </Form.Item>

        <Form.Item
          label={<span className="font-azonix !font-normal text-white uppercase tracking-widest">Email Address</span>}
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            className="bg-zinc-800 font-coda border-zinc-700 text-white"
            placeholder="Your email address"
          />
        </Form.Item>

        <Form.Item
          label={<span className="font-azonix !font-normal text-white uppercase tracking-widest">Phone Number</span>}
          name="phone"
          rules={[
            { required: true, message: 'Please enter your phone number' },
            {
              pattern: /^(\+1\s?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/,
              message: 'Please enter a valid US phone number',
            },
          ]}
        >
          <InputMask
            mask="usphone"
            className="bg-zinc-800 font-coda border-zinc-700 text-white"
            placeholder="(555) 123-4567"
          />
        </Form.Item>

        {/* Service Selection */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={<span className="font-azonix text-white !font-normal uppercase tracking-widest">Service Type</span>}
              name="serviceType"
            >
              <Select
                placeholder="Select service type"
                className="w-full"
                rootClassName="ant-select-dark"
                options={[
                  { label: 'Snow Removal', value: 'Snow Removal' },
                  { label: 'Landscaping', value: 'Landscaping' },
                  { label: 'Construction', value: 'Construction' },
                  { label: 'Trucking', value: 'Trucking' },
                  { label: 'Equipment Rental', value: 'Equipment Rental' },
                  { label: 'Other', value: 'Other' },
                ]}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label={<span className="font-azonix text-white !font-normal uppercase tracking-widest">Project Type</span>}
              name="projectType"
            >
              <Select
                placeholder="Select project type"
                className="w-full"
                rootClassName="ant-select-dark"
                options={[
                  { label: 'Residential', value: 'Residential' },
                  { label: 'Commercial', value: 'Commercial' },
                  { label: 'Industrial', value: 'Industrial' },
                  { label: 'Municipal', value: 'Municipal' },
                  { label: 'Other', value: 'Other' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Message */}
        <Form.Item
          label={<span className="font-azonix !font-normal text-white uppercase tracking-widest">Message</span>}
          name="message"
          rules={[{ required: true, message: 'Please enter your message' }]}
        >
          <TextArea
            rows={4}
            className="bg-zinc-800 font-coda text-white"
            placeholder="Your message"
          />
        </Form.Item>

        {/* Submit Button */}
        <div className="text-center pt-4 pb-4">
          <button
            type="submit"
            className="btn-single-cut-dark-text font-azonix text-xl tracking-wider px-8 py-3 cursor-pointer"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
          </button>
        </div>
      </Form>
    </Modal>
  );
};


